import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatDateRu } from "../../../utils/appointmentLabels";
import clinicAPI from "../../../services/clinicApi";
import { sortClubLifeFeedPosts } from "../../../utils/clubLifeFeedSort";
import ClubLifePostCard from "../common/ClubLifePostCard";
import ClientOverviewSection from "./ClientOverviewSection";
import ClientNotesSection from "./ClientNotesSection";
import ClientChildrenStrip from "./ClientChildrenStrip";
import {
  ClientMembershipsSection,
  ClientAttendanceSection,
  ClientEventsSection,
} from "./ClientProfileSections";
import AdminGuardiansPanel from "./AdminGuardiansPanel";
import {
  CLIENT_PROFILE_ANCHORS,
  CLIENT_PROFILE_TAB_TO_ANCHOR,
  PATIENT_TAB_ALIASES,
} from "./patientConstants";
import {
  clientProfileSubtitle,
  formatAttendanceSummary,
  formatMembershipDetail,
} from "./clientSportDisplay";
import { personDisplayName } from "../../../utils/clientAppUtils";
import { useClubLifeInteractions } from "../../../hooks/useClubLifeInteractions";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import {
  mediaUrl,
  TRAINER_COLOR_PRESETS,
} from "../../../utils/clientHeroTheme";
import { HubSegment } from "../AdminPortal/HubPwa";
import { Avatar, Skeleton } from "../../ui";
import ca from "../PatientPortal/clientApp.module.css";
import cs from "../clinic.module.css";
import md from "../common/manageDesktop.module.css";

const DEFAULT_TAB = "wall";
const SOCIAL_TABS = new Set(["wall", "achievements"]);

function themeStyle(themeId) {
  const preset = TRAINER_COLOR_PRESETS.find((p) => p.id === themeId);
  if (!preset) return {};
  return {
    "--member-accent": preset.color,
    "--ca-accent": preset.color,
  };
}

function clearanceStripLabel(edit) {
  const exp = edit?.medical_clearance_expires_at;
  if (!exp) return "не задан";
  const iso = `${String(exp).slice(0, 10)}T12:00:00`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "не задан";
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  const until = formatDateRu(iso, { day: "numeric", month: "short" });
  if (days < 0) return "просрочен";
  if (days <= 14) return `до ${until}`;
  return `до ${until}`;
}

function clearanceStripHint(edit) {
  const exp = edit?.medical_clearance_expires_at;
  if (!exp) return null;
  const days = Math.ceil(
    (new Date(`${String(exp).slice(0, 10)}T12:00:00`).getTime() - Date.now()) /
      86400000,
  );
  if (days < 0) return "нужно обновить";
  if (days <= 14) return "истекает скоро";
  return "действует";
}

export default function ClientProfileSocialView({
  patientId,
  patient,
  profile,
  edit,
  setEdit,
  sport,
  onSave,
  onArchive,
  onRestore,
  onSoftDelete,
  onReload,
  writeMessage,
  subdomain,
  newPassword,
  setNewPassword,
  onSavePortalEmail,
  onSetPortalPassword,
  savingPortalEmail,
  settingPortalPassword,
  onAddToCompetition,
}) {
  const [searchParams] = useSearchParams();
  const isApp = useAdminHubUi();
  const { heroStyle } = useClubPortalBranding("aa", null, true);
  const [socialProfile, setSocialProfile] = useState(null);
  const [socialLoading, setSocialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const [wallPosts, setWallPosts] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const isMobile = useIsMobile(768);
  const isDesktop = !isApp && !isMobile;

  const membership = formatMembershipDetail(sport?.membership);
  const attendance = formatAttendanceSummary(
    profile?.attendance_month || sport?.attendance_month,
  );
  const clubCard = socialProfile?.club_card;
  const primaryGroup =
    clubCard?.primary_group ||
    socialProfile?.groups?.[0]?.name ||
    profile?.groups?.[0]?.name ||
    null;

  const shellStyle = useMemo(
    () => themeStyle(socialProfile?.profile_theme),
    [socialProfile?.profile_theme],
  );

  const {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  } = useClubLifeInteractions(
    activeTab === "wall" ? setWallPosts : setAchievements,
    { commentsApi: "admin" },
  );

  const loadSocialProfile = useCallback(async () => {
    if (!patientId) return;
    setSocialLoading(true);
    try {
      const data = await clinicAPI.getStaffClientSocialProfile(patientId);
      setSocialProfile(data.profile || null);
    } catch {
      setSocialProfile(null);
    } finally {
      setSocialLoading(false);
    }
  }, [patientId]);

  const loadPosts = useCallback(async () => {
    if (!patientId || !SOCIAL_TABS.has(activeTab)) return;
    setPostsLoading(true);
    try {
      if (activeTab === "wall") {
        const data = await clinicAPI.getStaffClientSocialWall(patientId);
        setWallPosts(sortClubLifeFeedPosts(data.posts || []));
      } else if (activeTab === "achievements") {
        const data = await clinicAPI.getStaffClientSocialAchievements(patientId);
        setAchievements(sortClubLifeFeedPosts(data.posts || []));
      }
    } catch {
      if (activeTab === "wall") setWallPosts([]);
      else setAchievements([]);
    } finally {
      setPostsLoading(false);
    }
  }, [activeTab, patientId]);

  const profileTabs = useMemo(() => {
    const guardian =
      sport?.account_kind === "guardian" || sport?.also_guardian;
    return guardian
      ? CLIENT_PROFILE_ANCHORS.filter((t) => t.id !== "parents")
      : CLIENT_PROFILE_ANCHORS;
  }, [sport?.account_kind, sport?.also_guardian]);

  useEffect(() => {
    setActiveTab(DEFAULT_TAB);
    loadSocialProfile();
  }, [patientId, loadSocialProfile]);

  useEffect(() => {
    const guardian =
      sport?.account_kind === "guardian" || sport?.also_guardian;
    if (guardian && activeTab === "parents") {
      setActiveTab(DEFAULT_TAB);
    }
  }, [sport?.account_kind, sport?.also_guardian, activeTab]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (!tab) return;
    const resolved = PATIENT_TAB_ALIASES[tab] || tab;
    const anchor = CLIENT_PROFILE_TAB_TO_ANCHOR[resolved] || resolved;
    if (anchor) setActiveTab(anchor);
  }, [searchParams]);

  useEffect(() => {
    if (!patientId) return;
    if (SOCIAL_TABS.has(activeTab)) loadPosts();
  }, [activeTab, loadPosts, patientId]);

  useAdminRefresh(() => {
    loadSocialProfile();
    onReload?.();
    if (SOCIAL_TABS.has(activeTab)) loadPosts();
  });

  const archived =
    socialProfile?.is_archived || edit?.is_archived || patient?.patient_card?.is_archived;
  const statusLabel = archived
    ? "В архиве"
    : socialProfile?.is_blocked || patient?.is_blocked
      ? "Заблокирован"
      : "Активен";
  const subtitle = clientProfileSubtitle(sport);
  const phone = socialProfile?.phone || patient?.phone;
  const displayName = personDisplayName(
    socialProfile?.full_name || patient?.full_name,
  ) || "Клиент";
  const coverUrl = mediaUrl(socialProfile?.profile_cover_url);
  const posts = activeTab === "wall" ? wallPosts : achievements;
  const isGuardianAccount =
    sport?.account_kind === "guardian" || sport?.also_guardian;
  const familyChildren =
    profile?.linked_athletes?.length || sport?.children?.length || 0;

  const overviewBlock = (
    <div className={cs.staffClientSocialOverview}>
      <ClientOverviewSection
        patientId={patientId}
        patient={patient}
        profile={profile}
        guardians={profile?.guardians}
        edit={edit}
        setEdit={setEdit}
        onSave={onSave}
        subdomain={subdomain}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSavePortalEmail={onSavePortalEmail}
        onSetPortalPassword={onSetPortalPassword}
        savingPortalEmail={savingPortalEmail}
        settingPortalPassword={settingPortalPassword}
        editingInfo={editingInfo}
        onToggleEdit={() => setEditingInfo((v) => !v)}
        onAddToCompetition={onAddToCompetition}
        social={!isApp}
        compact={isApp}
      />
    </div>
  );

  const statusBadges = (
    <>
      <span
        className={
          archived
            ? cs.staffClientSocialArchivedDesktop
            : cs.staffClientSocialActiveDesktop
        }
      >
        {statusLabel}
      </span>
      {isGuardianAccount ? (
        <span className={cs.staffClientSocialGuardianTagDesktop}>
          {sport?.account_kind === "guardian" && !sport?.groups?.length
            ? "Родитель"
            : familyChildren
              ? `Родитель · ${familyChildren}`
              : "Родитель"}
        </span>
      ) : null}
      {subtitle ? (
        <span className={cs.staffClientSocialSubtitleDesktop}>{subtitle}</span>
      ) : null}
    </>
  );

  const actionButtons = (
    <>
      {phone ? (
        <a
          href={`tel:${phone}`}
          className={isDesktop ? ca.memberBtnPrimary : cs.staffClientSocialBtnPrimary}
        >
          Позвонить
        </a>
      ) : null}
      <button
        type="button"
        className={isDesktop ? ca.memberBtnOutline : cs.staffClientSocialBtnGhost}
        onClick={writeMessage}
      >
        Написать
      </button>
      {!archived && onArchive ? (
        <div className={cs.staffClientSocialMenuWrap}>
          <button
            type="button"
            className={cs.staffClientSocialBtnIcon}
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label="Ещё действия"
          >
            ⋯
          </button>
          {menuOpen ? (
            <div className={cs.staffClientSocialMenu}>
              {isMobile && !isGuardianAccount ? (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setActiveTab("parents");
                  }}
                >
                  Родители
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onArchive();
                }}
              >
                В архив
              </button>
              {onSoftDelete ? (
                <button
                  type="button"
                  className={cs.staffClientSocialMenuDanger}
                  onClick={() => {
                    setMenuOpen(false);
                    onSoftDelete();
                  }}
                >
                  Удалить
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : archived && (onRestore || onSoftDelete) ? (
        <div className={cs.staffClientSocialMenuWrap}>
          <button
            type="button"
            className={cs.staffClientSocialBtnIcon}
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label="Ещё действия"
          >
            ⋯
          </button>
          {menuOpen ? (
            <div className={cs.staffClientSocialMenu}>
              {onRestore ? (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onRestore();
                  }}
                >
                  Вернуть из архива
                </button>
              ) : null}
              {onSoftDelete ? (
                <button
                  type="button"
                  className={cs.staffClientSocialMenuDanger}
                  onClick={() => {
                    setMenuOpen(false);
                    onSoftDelete();
                  }}
                >
                  Удалить
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  const clubStrip = isDesktop ? (
    <div className={ca.memberClubStrip}>
      <button
        type="button"
        className={ca.memberClubTile}
        onClick={() => setActiveTab("client-overview")}
      >
        <span className={ca.memberClubTileLabel}>Группа</span>
        <strong className={ca.memberClubTileValue}>{primaryGroup || "—"}</strong>
      </button>
      <button
        type="button"
        className={ca.memberClubTile}
        onClick={() => setActiveTab("client-attendance")}
      >
        <span className={ca.memberClubTileLabel}>Месяц</span>
        <strong className={ca.memberClubTileValue}>{attendance.label}</strong>
        {attendance.hint ? (
          <small className={ca.memberClubTileHint}>{attendance.hint}</small>
        ) : null}
      </button>
      <button
        type="button"
        className={ca.memberClubTile}
        onClick={() => setActiveTab("client-memberships")}
      >
        <span className={ca.memberClubTileLabel}>Абонемент</span>
        <strong className={ca.memberClubTileValue}>
          {clubCard?.membership_label || membership.title}
        </strong>
        {membership.meta ? (
          <small className={ca.memberClubTileHint}>{membership.meta}</small>
        ) : null}
      </button>
      <button
        type="button"
        className={ca.memberClubTile}
        onClick={() => setActiveTab("client-overview")}
      >
        <span className={ca.memberClubTileLabel}>Допуск</span>
        <strong className={ca.memberClubTileValue}>{clearanceStripLabel(edit)}</strong>
        {clearanceStripHint(edit) ? (
          <small className={ca.memberClubTileHint}>{clearanceStripHint(edit)}</small>
        ) : null}
      </button>
    </div>
  ) : (
    <div className={cs.staffClientSocialStrip}>
      <button
        type="button"
        className={cs.staffClientSocialStatCard}
        onClick={() => setActiveTab("client-overview")}
      >
        <span className={cs.staffClientSocialStatLabel}>Группа</span>
        <strong>{primaryGroup || "—"}</strong>
      </button>
      <button
        type="button"
        className={cs.staffClientSocialStatCard}
        onClick={() => setActiveTab("client-attendance")}
      >
        <span className={cs.staffClientSocialStatLabel}>Месяц</span>
        <strong>{attendance.label}</strong>
        {attendance.hint ? <small>{attendance.hint}</small> : null}
      </button>
      <button
        type="button"
        className={cs.staffClientSocialStatCard}
        onClick={() => setActiveTab("client-memberships")}
      >
        <span className={cs.staffClientSocialStatLabel}>Абонемент</span>
        <strong>{clubCard?.membership_label || membership.title}</strong>
        {membership.meta ? <small>{membership.meta}</small> : null}
      </button>
      <button
        type="button"
        className={cs.staffClientSocialStatCard}
        onClick={() => setActiveTab("client-overview")}
      >
        <span className={cs.staffClientSocialStatLabel}>Допуск</span>
        <strong>{clearanceStripLabel(edit)}</strong>
        {clearanceStripHint(edit) ? <small>{clearanceStripHint(edit)}</small> : null}
      </button>
    </div>
  );

  const tabContent = (
    <>
      {activeTab === "wall" || activeTab === "achievements" ? (
        postsLoading ? (
          <Skeleton height={160} style={{ borderRadius: 16, margin: "12px 0" }} />
        ) : posts.length === 0 ? (
          <div className={ca.memberFeedEmpty}>
            <span>{activeTab === "wall" ? "📝" : "🏆"}</span>
            <p>
              {activeTab === "wall"
                ? "На стене пока нет записей"
                : "Достижений пока нет"}
            </p>
          </div>
        ) : (
          <div className={ca.memberPosts}>
            {posts.map((post) => (
              <ClubLifePostCard
                key={post.id}
                post={post}
                mode="admin"
                onLike={toggleLike}
                onLoadComments={loadComments}
                commentsOpen={openComments === post.id}
                comments={commentsByPost[post.id] || []}
                onComment={addComment}
                commentBusy={commentBusy}
              />
            ))}
          </div>
        )
      ) : null}
      {activeTab === "parents" ? (
        <div className={cs.staffClientSocialParentsTab} id="client-guardians">
          <AdminGuardiansPanel
            patientId={patientId}
            onChanged={onReload}
            compact={!isDesktop}
            layout={isDesktop ? "desktop" : "mobile"}
          />
        </div>
      ) : null}
      {activeTab === "client-overview" ? overviewBlock : null}
      {activeTab === "client-memberships" ? (
        <ClientMembershipsSection
          patientId={patientId}
          profile={profile}
          onChanged={onReload}
          compact
        />
      ) : null}
      {activeTab === "client-attendance" ? (
        <ClientAttendanceSection patientId={patientId} />
      ) : null}
      {activeTab === "client-events" ? (
        <ClientEventsSection patientId={patientId} profile={profile} compact />
      ) : null}
      {activeTab === "client-notes" ? (
        <ClientNotesSection
          patientId={patientId}
          legacyNote={patient?.internal_notes}
        />
      ) : null}
    </>
  );

  const tabNav = isApp ? (
    <HubSegment
      tabs={profileTabs}
      active={activeTab}
      onChange={setActiveTab}
      className={cs.staffClientSocialSegment}
    />
  ) : (
    <nav
      className={isDesktop ? ca.memberTabs : cs.staffClientSocialTabs}
      aria-label="Разделы профиля"
    >
      {profileTabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className={
            activeTab === t.id
              ? isDesktop
                ? ca.memberTabOn
                : cs.staffClientSocialTabOn
              : isDesktop
                ? ca.memberTab
                : cs.staffClientSocialTab
          }
          onClick={() => setActiveTab(t.id)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );

  if (socialLoading && !socialProfile) {
    return <Skeleton height={320} style={{ borderRadius: 16 }} />;
  }

  const mobileStatsLine = (
    <p className={ca.memberVkSubline}>
      <strong>{socialProfile?.wall_posts_count ?? 0}</strong> записей ·{" "}
      <strong>{socialProfile?.followers_count ?? 0}</strong> подписчиков ·{" "}
      <strong>{socialProfile?.following_count ?? 0}</strong> подписок
    </p>
  );

  const coverBlock = (
    <div className={ca.memberCoverWrap}>
      <div className={`${ca.memberCover} ${cs.staffClientSocialCover}`}>
        <div
          className={ca.memberCoverBg}
          style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : heroStyle}
        />
      </div>
    </div>
  );

  const desktopStatsLine = (
    <p className={ca.memberVkSubline}>
      <strong>{socialProfile?.wall_posts_count ?? 0}</strong> записей ·{" "}
      <strong>{socialProfile?.followers_count ?? 0}</strong> подписчиков ·{" "}
      <strong>{socialProfile?.following_count ?? 0}</strong> подписок
    </p>
  );

  return (
    <div
      className={`${ca.memberProfilePage} ${cs.staffClientSocial} ${
        isDesktop ? `${md.desktopOwnerMemberPage} ${cs.staffClientSocialDesktop}` : ""
      }`.trim()}
      style={shellStyle}
    >
      {isDesktop ? (
        <header
          className={`${ca.memberVkHeader} ${md.desktopMemberVkHeader}`}
        >
          {coverBlock}
          <div className={`${ca.memberVkProfile} ${md.desktopMemberVkProfile}`}>
            <div className={ca.memberVkTopRow}>
              <div className={ca.memberVkAvatarWrap}>
                <Avatar
                  name={displayName}
                  src={mediaUrl(socialProfile?.photo_url || patient?.photo_url)}
                  size="lg"
                  className={ca.memberVkAvatar}
                />
              </div>
              <div className={ca.memberVkTitleBlock}>
                <h1 className={ca.memberVkName}>{displayName}</h1>
                <p className={cs.staffClientSocialMetaDesktop}>{statusBadges}</p>
                {desktopStatsLine}
                {socialProfile?.profile_bio ? (
                  <p className={ca.memberVkBio}>{socialProfile.profile_bio}</p>
                ) : null}
              </div>
              <div className={`${ca.memberVkHeadActions} ${md.desktopVkHeadActions}`}>
                {actionButtons}
              </div>
            </div>

            {clubStrip}
          </div>
        </header>
      ) : (
        <header className={ca.memberVkHeader}>
          {coverBlock}
          <div className={ca.memberVkProfile}>
            <div className={ca.memberVkTopRow}>
              <div className={ca.memberVkAvatarWrap}>
                <Avatar
                  name={displayName}
                  src={mediaUrl(socialProfile?.photo_url || patient?.photo_url)}
                  size="lg"
                  className={ca.memberVkAvatar}
                />
              </div>
              <div className={ca.memberVkTitleBlock}>
                <h1 className={ca.memberVkName}>{displayName}</h1>
                <p className={cs.staffClientSocialMetaMobile}>{statusBadges}</p>
                {mobileStatsLine}
              </div>
            </div>

            <div className={cs.staffClientSocialMobileActions}>{actionButtons}</div>

            {socialProfile?.profile_bio ? (
              <p className={`${ca.memberVkBio} ${cs.staffClientSocialMobileBio}`}>
                {socialProfile.profile_bio}
              </p>
            ) : null}

            {clubStrip}

            {isGuardianAccount ? (
              <div className={cs.staffClientSocialFamily}>
                <ClientChildrenStrip profile={profile} sport={sport} />
              </div>
            ) : null}
          </div>
        </header>
      )}

      <div
        className={
          isDesktop
            ? `${ca.memberBody} ${md.desktopMemberBody} ${cs.staffClientSocialBody}`
            : ca.memberBody
        }
      >
        {isDesktop && isGuardianAccount ? (
          <div className={cs.staffClientSocialFamily}>
            <ClientChildrenStrip profile={profile} sport={sport} />
          </div>
        ) : null}

        {tabNav}

        <div className={cs.staffClientSocialTabPanel}>{tabContent}</div>
      </div>
    </div>
  );
}
